import { Box, ButtonGroup, Card, Grid, Icon, CircularProgress, Autocomplete, TextField, Chip, Menu, MenuItem, Tabs, styled, Tab, Stack, IconButton, Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import PropTypes from "prop-types";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import { useNavigate } from "react-router-dom";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { wrapper } from "utils/wrapper";
import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import Divider from "@material-ui/core/Divider";
import { PhotoProvider, PhotoView } from "react-photo-view";
import SoftBadge from "components/SoftComponents/SoftBadge";
import SoftSelect from "components/SoftComponents/SoftSelect";
import Swal from "sweetalert2";
import moment from "moment";

import api from "services/api/campaign";
import sitesAPI from "services/api/sites";
import gtfsAPI from "services/api/gtfs"
import AddIcon from '@mui/icons-material/Add';

import { CampaignPreviewToggle } from "pages/components/Campaigns/CampaignsAll/CampaignPreview";
import theme from "assets/theme";

const FolderTab = styled(Tab, { shouldForwardProp: (prop) => prop !== 'tabColour' && prop !== 'active' && prop !== 'deviceLayout' && prop !== 'isVisible' })(({ tabColour, active, deviceLayout, isVisible }) => ({
    textTransform: 'none !important',
    justifyContent: deviceLayout != 'mobile' ? 'center' : 'flex-start',
    fontSize: '0.95rem !important',
    color: '#627594 !important',
    minHeight: '48px !important',
    padding: '12px 15px !important',
    marginRight: '-15px !important',
    // backgroundColor: active ? '#fff !important' : '#f7f7f7 !important',
    zIndex: active ? 2 : 1,
    borderRadius: '15px 15px 0 0 !important',
    transition: 'all 0.2s ease-in-out !important',
    border: '1px solid #e0e0e0',
    borderBottom: active ? '1px solid #fff !important' : '1px solid #e0e0e0', // This creates the "open" effect
    backgroundColor: active ? '#fff !important' : '#f0f0f0 !important',
    marginBottom: '-1px',

    '&.Mui-selected': {
        color: '#334155 !important',
        fontWeight: '600 !important',
        width: deviceLayout == 'mobile' ? '100%' : 'unset'
    },

    [theme.breakpoints.down('xl')]: {
        display: isVisible ? 'inline-flex' : 'none !important',
    }
}));

// --- CAMPAIGN TAG FILTERING LOGIC ---
const doesCampaignMatchTags = (campaign, selectedTags) => {
    if (!selectedTags || selectedTags.length === 0) {
        return true;
    }

    const campaignTags = campaign.deviceFilter || [];
    if (campaignTags.length === 0) {
        return true;
    }

    return campaignTags.some(campTag =>
        selectedTags.some(uiTag =>
            uiTag.field === campTag.field && uiTag.value === campTag.value
        )
    );
};

// --- SCHEDULE FORMATTING HELPERS ---
const formatMinutes = (mins) => {
    if (typeof mins !== 'number') return '';
    const h = Math.floor(mins / 60).toString().padStart(2, '0');
    const m = (mins % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
};

const formatDays = (daysObj) => {
    if (!daysObj) return '';
    const activeDays = Object.keys(daysObj).filter(d => daysObj[d]);
    if (activeDays.length === 7) return 'Everyday';
    if (activeDays.length === 0) return 'No days set';
    const dayMap = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
    return activeDays.map(d => dayMap[d]).join(', ');
};

const getScheduleSummary = (campaign) => {
    if (!campaign || !campaign.scheduleType) return "Not Scheduled";

    if (campaign.scheduleType === 'alwaysOn') return "Always On";

    if (campaign.scheduleType === 'startEnd') {
        const slots = campaign.scheduling?.startEnd || [];
        if (!slots.length) return "No dates configured";
        const first = slots[0];
        const start = moment(first.startTime).format('MMM D, HH:mm');
        const end = moment(first.endTime).format('MMM D, HH:mm');
        return `${start} - ${end}${slots.length > 1 ? ` (+${slots.length - 1})` : ''}`;
    }

    if (campaign.scheduleType === 'recurring') {
        const slots = campaign.scheduling?.recurring || [];
        if (!slots.length) return "No times configured";
        const first = slots[0];
        const daysStr = formatDays(first.days);
        const timeStr = `${formatMinutes(first.startTime)} - ${formatMinutes(first.endTime)}`;
        return `${daysStr} • ${timeStr}${slots.length > 1 ? ` (+${slots.length - 1})` : ''}`;
    }

    if (campaign.scheduleType === 'recurringWithDate') {
        const slots = campaign.scheduling?.recurringWithDate || [];
        if (!slots.length) return "No times configured";
        const first = slots[0];
        const daysStr = formatDays(first.days);
        const timeStr = `${formatMinutes(first.startTime)} - ${formatMinutes(first.endTime)}`;
        const dateStr = `${moment(first.startDate).format('MMM D')} - ${moment(first.endDate).format('MMM D')}`;
        return `${daysStr} • ${timeStr} (${dateStr})${slots.length > 1 ? ` (+${slots.length - 1})` : ''}`;
    }

    return "Scheduled";
};

// --- ACTIVE RIGHT NOW LOGIC ---
const checkIfPlayingNow = (campaign) => {
    if (!campaign.enabled) return false;
    if (campaign.scheduleType === 'alwaysOn') return true;

    const now = moment();
    const currentMins = now.hours() * 60 + now.minutes();
    const currentDay = now.format('dddd').toLowerCase();

    if (campaign.scheduleType === 'startEnd') {
        const slots = campaign.scheduling?.startEnd || [];
        return slots.some(slot => now.isBetween(moment(slot.startTime), moment(slot.endTime)));
    }

    if (campaign.scheduleType === 'recurring') {
        const slots = campaign.scheduling?.recurring || [];
        return slots.some(slot =>
            slot.days?.[currentDay] && currentMins >= slot.startTime && currentMins <= slot.endTime
        );
    }

    if (campaign.scheduleType === 'recurringWithDate') {
        const slots = campaign.scheduling?.recurringWithDate || [];
        return slots.some(slot => {
            const withinDates = now.isSameOrAfter(moment(slot.startDate).startOf('day')) &&
                now.isSameOrBefore(moment(slot.endDate).endOf('day'));
            const withinTime = currentMins >= slot.startTime && currentMins <= slot.endTime;
            const correctDay = slot.days?.[currentDay];
            return withinDates && withinTime && correctDay;
        });
    }

    return false;
};

const CampaignContentItem = ({ opt, campaignType, handleToggleStatus, handleDelete, toggleStatusMutation, deleteCampaignMutation, setViewedCampaign }) => (
    <PhotoProvider>
        <Card
            sx={({ palette: { white }, functions: { rgba } }) => ({
                height: '100%',
                background: opt.enabled ? '#fff !important' : "#eee !important",
                boxShadow: checkIfPlayingNow(opt) ? `0 0 15px 2px ${rgba(campaignType.colour, 0.4)}` : "none",
                border: `1px solid ${rgba(campaignType.colour, 0.5)}`
            })}
        >
            <SoftBox p={2} height="100%" display="flex" flexDirection="column" justifyContent="space-between">
                <SoftBox>
                    <CampaignPreviewToggle {...{ opt, campaignType }} />
                </SoftBox>
                <SoftBox>
                    <SoftBox width="100%" pb={1} display="flex" justifyContent="space-between" gap={1}>
                        {checkIfPlayingNow(opt) && (
                            <SoftBadge
                                variant="gradient"
                                color="dark"
                                badgeContent="▶ Live Now"
                                container
                            />
                        )}
                    </SoftBox>
                    <SoftBox p={1.5} pb={1} display="flex" flexDirection='column' sx={{ borderRadius: '10px', backgroundColor: '#e6e6e6' }}>
                        <SoftTypography variant="button" sx={{ fontSize: '18px' }}>{opt.name}</SoftTypography>
                        <SoftTypography variant="caption" sx={{ fontSize: '13px', lineHeight: '1.5', mt: 0.5, color: '#666', display: 'flex', alignItems: 'center' }}>
                            <Icon sx={{ fontSize: '14px', mr: 0.5 }}>schedule</Icon>
                            {getScheduleSummary(opt)}
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox pt={1} pb={2}><Divider /></SoftBox>
                    <SoftBox>
                        <Grid container spacing={1}>
                            <Grid item xxs={12} xs={4}>
                                <SoftButton variant="outlined" color="dark" size="small" fullWidth onClick={() => setViewedCampaign(opt._id)}>
                                    <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>settings</Icon> Edit
                                </SoftButton>
                            </Grid>
                            <Grid item xxs={12} xs={4}>
                                <SoftButton
                                    variant="outlined"
                                    color={opt.enabled ? "dark" : "success"}
                                    size="small"
                                    fullWidth
                                    onClick={() => handleToggleStatus(opt._id)}
                                    disabled={toggleStatusMutation.isPending}
                                >
                                    <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>power_settings_new</Icon>
                                    {opt.enabled ? "Disable" : "Enable"}
                                </SoftButton>
                            </Grid>
                            <Grid item xxs={12} xs={4}>
                                <SoftButton
                                    variant="outlined"
                                    color="dark"
                                    size="small"
                                    fullWidth
                                    onClick={() => handleDelete(opt)}
                                    disabled={deleteCampaignMutation.isPending}
                                >
                                    <Icon sx={{ paddingBottom: '3px', paddingRight: '5px' }}>delete</Icon> Delete
                                </SoftButton>
                            </Grid>
                        </Grid>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </Card>
    </PhotoProvider>
);

function AllCampaignsView({ map, user, initialTags = [], isEmbedded = false }) {
    if (map) { disableMap(map, false); }
    api.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0());
    gtfsAPI.loadToken(useAuth0());
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    // const [showTypes, setShowTypes] = useState({});
    const [filtersExpanded, setFiltersExpanded] = useState(true);
    const [selectedTabId, setSelectedTabId] = useState(0);
    const [tags, setTags] = useState(initialTags);
    const [campaignFilter, setCampaignFilter] = useState({ value: 1, label: "All" });

    const [anchorEl, setAnchorEl] = useState(null);
    const openMenu = Boolean(anchorEl);
    const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    const setViewedCampaign = (id) => navigate(`/campaign/${id}`);

    // const toggleShowTypes = (optionId) => {
    //     setShowTypes((prevMap) => ({
    //         ...prevMap,
    //         // If it's undefined, we assume it was 'true' by default, so set it to false
    //         [optionId]: prevMap[optionId] === false ? true : false,
    //     }));
    // };

    // --- QUERIES ---
    const campaigns = useQuery({
        queryKey: ['campaigns'],
        queryFn: () => api.getAllCampaigns(),
        staleTime: 5 * 60 * 1000,
    });

    const campaignTypes = useQuery({
        queryKey: ['campaignTypes'],
        queryFn: () => api.getCampaignTypes(),
        staleTime: 5 * 60 * 1000,
    });

    const available_tags = useQuery({
        queryKey: ['getAssignedTags'],
        queryFn: async () => {
            return await gtfsAPI.getAssignedTags();
        }
    });

    // --- MUTATIONS ---
    const toggleStatusMutation = useMutation({
        mutationFn: (id) => api.toggleCampaignStatus(id),
        onSuccess: (updatedCampaign) => {
            queryClient.setQueryData(['campaigns'], (oldCampaigns) => {
                if (!oldCampaigns) return [];
                return oldCampaigns.map(campaign =>
                    campaign._id === updatedCampaign._id ? updatedCampaign : campaign
                );
            });
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        },
        onError: (error) => {
            console.error("Failed to toggle campaign status:", error);
            Swal.fire(`<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Error</div>`, `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Could not update campaign status.</div>`, "error");
        }
    });

    const deleteCampaignMutation = useMutation({
        mutationFn: (id) => api.deleteCampaign(id),
        onSuccess: () => {
            Swal.fire('<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Deleted!</div>', '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">The campaign has been removed.</div>', 'success');
            queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        }
    });

    // --- HANDLERS ---
    const handleToggleStatus = (id) => {
        toggleStatusMutation.mutate(id);
    };

    const handleDelete = (campaign) => {
        Swal.fire({
            title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px">Are you sure?</div>',
            html: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">You are about to delete "${campaign.name}". This cannot be undone.</div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#cb0c9f',
            cancelButtonColor: '#8392ab',
            confirmButtonText: `<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:16px">Yes, delete it!</div>`
        }).then((result) => {
            if (result.isConfirmed) {
                deleteCampaignMutation.mutate(campaign._id);
            }
        });
    };

    const handleAddCampaign = (campaignType) => {
        navigate(`/campaign/new?typeId=${campaignType._id}`);
    };

    const handleChange = (event, newValue) => {
        setSelectedTabId(newValue);
    };

    const transport_options = [
        ...(available_tags.data?.routes || []).map((route) => ({
            category: 'Route', field: 'route_id', value: route.route_short_name, label: `Route ${route.route_short_name} - ${route.route_long_name}`
        })),
        ...(available_tags.data?.stops || []).map((r) => ({
            category: 'Stop', field: 'stop_id', value: r.stop_id, label: `Stop ${r.stop_id} - ${r.gtfs.stop_name}`
        }))
    ];

    const tabList = [
        { label: "All", _id: "all", colour: "#344767" },
        ...(campaignTypes.data || [])
    ];

    const filter_options = [
        { value: 1, label: "All" },
        { value: 2, label: "Enabled" },
        { value: 3, label: "Disabled" }
    ];

    // const view_options = [
    //     { value: 'folder', label: 'Folder' },
    //     { value: 'individual', label: 'Individual' },
    //     { value: 'list', label: 'List' }
    // ];

    const currentCampaigns = (
        <SoftBox /*mx={isEmbedded ? 0 : 1}*/ display="flex" flexDirection="column" borderRadius="10px" color={"white"} width="100%" height={isEmbedded ? "calc(100svh - 10.25rem - 16px)" : "100%"} /*sx={({ functions: { rgba } }) => ({ backgroundColor: rgba("#dcdcdc", 1) })}*/ >
            <SoftBox /*pt={1} px={1}*/ height="100%" sx={({ functions: { rgba } }) => ({ borderRadius: '10px 10px 0 0' })}>
                <SoftBox px={isEmbedded ? 0 : 1} height="100%" display="flex" flexDirection="column">
                    <SoftBox mb={1}>
                        <Accordion
                            expanded={filtersExpanded}
                            onChange={() => setFiltersExpanded(!filtersExpanded)}
                            sx={{ borderRadius: "12px !important", boxShadow: "none", border: "1px solid #e0e0e0" }}
                        >
                            <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                                <SoftBox display='flex' flexDirection={{ xxs: 'column', xs: 'row' }} justifyContent={{ xxs: "center", xs: 'space-between' }} alignItems="center" width="100%">
                                    <SoftBox height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems={{ xxs: 'center', xs: 'flex-start' }}>
                                        <SoftTypography variant="h5" fontWeight="bold" textAlign="center">Campaign Manager</SoftTypography>
                                        {deviceLayout != 'mobile' && (
                                            <SoftTypography variant="body3">For Your Organisation</SoftTypography>
                                        )}
                                    </SoftBox>
                                    <SoftBox display="flex" alignItems="center" pr={{ xxs: 0, xs: 2 }}>
                                        <SoftTypography variant="button" textAlign="center">
                                            {/* <Icon sx={{ verticalAlign: 'middle', mr: 1 }}>filter_list</Icon> */}
                                            Filter by Tags & Status
                                        </SoftTypography>
                                    </SoftBox>
                                </SoftBox>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    <Grid item xxs={12} xl={8}>
                                        <SoftBox display="flex" flexDirection="column" mr={{ xxs: 0, xl: 0 }}>
                                            <SoftBox mb={0.5} pl={0.5}>
                                                <SoftTypography variant="caption" fontWeight="bold">Tags</SoftTypography>
                                            </SoftBox>
                                            <SoftBox width="100%">
                                                <Autocomplete
                                                    multiple
                                                    id="transport-targets"
                                                    options={transport_options}
                                                    groupBy={(option) => option.category}
                                                    getOptionLabel={(option) => option.label}
                                                    isOptionEqualToValue={(option, val) => option.field === val.field && option.value === val.value}
                                                    value={tags || []}
                                                    onChange={(event, newValue) => { setTags(newValue); }}
                                                    renderTags={(tagValue, getTagProps) =>
                                                        tagValue.map((option, index) => {
                                                            const { key, ...tagProps } = getTagProps({ index });
                                                            return (
                                                                <Chip
                                                                    key={key}
                                                                    variant="outlined"
                                                                    label={`${option.category}: ${option.label || option.value}`}
                                                                    {...tagProps}
                                                                    sx={{
                                                                        transition: 'none !important',
                                                                        '& .MuiChip-label': { transition: 'none !important' }
                                                                    }}
                                                                />
                                                            );
                                                        })
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            variant="outlined"
                                                            placeholder="Select routes, stops, or devices..."
                                                            sx={{
                                                                "& .MuiOutlinedInput-root": {
                                                                    padding: `0.${tags.length ? 2 : 5}rem 0.75rem 0.${tags.length ? 2 : 5}rem 0.75rem !important`,
                                                                    transition: 'none !important',
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </SoftBox>
                                        </SoftBox>
                                    </Grid>
                                    <Grid item xxs={12} xl={4}>
                                        <SoftBox display="flex" flexDirection="column">
                                            <SoftBox mb={0.5} pl={0.5}>
                                                <SoftTypography variant="caption" fontWeight="bold">Campaign Status</SoftTypography>
                                            </SoftBox>
                                            <SoftBox sx={{ borderRadius: '10px', background: '#fff', width: '100%' }}>
                                                <SoftSelect size="medium" sx={{ height: 'calc(100% - 4px) !important' }} value={campaignFilter} options={filter_options} onChange={setCampaignFilter} />
                                            </SoftBox>
                                        </SoftBox>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    </SoftBox>

                    <SoftBox display="flex" flexDirection="column">
                        <Tabs
                            value={selectedTabId}
                            onChange={handleChange}
                            // Removes the default MUI blue underline indicator
                            TabIndicatorProps={{ style: { display: 'none' } }}
                            variant="scrollable"
                            scrollButtons={false}
                            sx={{
                                minHeight: '48px', width: '100%', padding: 0, borderRadius: 0, background: 'transparent',
                                '& .MuiTabs-flexContainer': {
                                    width: 'calc(100% - 14px)'
                                },
                                '& .MuiTabs-scroller': {
                                    overflow: 'visible !important',
                                    display: 'flex',
                                    justifyContent: { xxs: 'flex-start' },
                                    padding: { xxs: 0 }
                                }
                            }}
                        >
                            {tabList.map((tab, tab_index) => {
                                const isVisible = Math.abs(tab_index - selectedTabId) <= 1;

                                return (
                                    <FolderTab
                                        key={tab_index}
                                        value={tab_index}
                                        tabColour={tab.colour}
                                        active={selectedTabId === tab_index}
                                        deviceLayout={deviceLayout}
                                        isVisible={isVisible}
                                        label={
                                            <>
                                                {deviceLayout != 'mobile' ? (
                                                    <>
                                                        <SoftBox position="absolute" left={10} top='auto'>
                                                            {tab_index !== 0 && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (tab_index == selectedTabId) {
                                                                            console.log("Add button clicked for:", tab.label);
                                                                            handleAddCampaign(tab)
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        width: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                        height: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                        backgroundColor: tab.colour,
                                                                        color: '#fff',
                                                                        '&:hover': {
                                                                            backgroundColor: tab.colour,
                                                                            filter: tab_index == selectedTabId && 'brightness(0.9)',
                                                                            transform: tab_index == selectedTabId && 'scale(1.1)',
                                                                        },
                                                                        boxShadow: tab_index === selectedTabId ? `0 0 8px ${tab.colour}` : 'none',
                                                                        transition: 'all 0.2s ease',
                                                                    }}
                                                                >
                                                                    {tab_index === selectedTabId && (
                                                                        <AddIcon sx={{ fontSize: '1.5rem !important', color: `${'#fff'} !important`, marginRight: '0 !important' }} />
                                                                    )}
                                                                </IconButton>
                                                            )}
                                                        </SoftBox>
                                                        <SoftTypography variant="inherit">{tab.label}</SoftTypography>
                                                    </>
                                                ) : (
                                                    <SoftBox display="flex" flexDirection="row" alignItems="center" justifyContent="flex-start">
                                                        <SoftBox pr={2}>
                                                            {tab_index !== 0 && (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (tab_index == selectedTabId) {
                                                                            console.log("Add button clicked for:", tab.label);
                                                                            handleAddCampaign(tab)
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        width: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                        height: tab_index === selectedTabId ? '1.75rem' : '0.75rem',
                                                                        backgroundColor: tab.colour,
                                                                        color: '#fff',
                                                                        '&:hover': {
                                                                            backgroundColor: tab.colour,
                                                                            filter: tab_index == selectedTabId && 'brightness(0.9)',
                                                                            transform: tab_index == selectedTabId && 'scale(1.1)',
                                                                        },
                                                                        boxShadow: tab_index === selectedTabId ? `0 0 8px ${tab.colour}` : 'none',
                                                                        transition: 'all 0.2s ease',
                                                                    }}
                                                                >
                                                                    {tab_index === selectedTabId && (
                                                                        <AddIcon sx={{ fontSize: '1.5rem !important', color: `${'#fff'} !important`, marginRight: '0 !important' }} />
                                                                    )}
                                                                </IconButton>
                                                            )}
                                                        </SoftBox>
                                                        <SoftTypography variant="inherit">{tab.label}</SoftTypography>
                                                    </SoftBox>
                                                )}
                                            </>
                                        }
                                    />
                                )
                            })}
                        </Tabs>
                    </SoftBox>

                    {/* Main Content Grid */}
                    <SoftBox
                        flex={1}
                        sx={{
                            backgroundColor: "#fff",
                            borderRadius: "0 0 15px 15px",
                            mt: "-1px",
                            overflowY: "auto",
                            p: 3
                        }}
                    >
                        <Grid container spacing={3}>
                            {campaignTypes?.data && (() => {
                                const activeTab = tabList[selectedTabId];
                                const filtered = campaigns.data?.filter(c => {
                                    const matchesFolder = selectedTabId === 0 || c.campaignTypeId === activeTab._id;
                                    const matchesStatus = campaignFilter.value === 1 ||
                                        (campaignFilter.value === 2 && c.enabled) ||
                                        (campaignFilter.value === 3 && !c.enabled);
                                    return matchesFolder && matchesStatus && doesCampaignMatchTags(c, tags);
                                }) || [];

                                if (filtered.length === 0) {
                                    return (
                                        <SoftBox display="flex" justifyContent={{ xxs: 'center', lg: "flex-start" }} pl={2} pt={2}>
                                            <SoftTypography variant="body3" color="secondary" fontWeight="regular" sx={{ fontStyle: 'italic' }}>
                                                No campaigns found.
                                            </SoftTypography>
                                        </SoftBox>
                                    );
                                }

                                return filtered.map((opt) => (
                                    <Grid item key={opt._id || index} xxs={12} md={6} lg={4} xxl={3} x5l={2}>
                                        <CampaignContentItem opt={opt} campaignType={campaignTypes.data?.find(t => t._id === opt.campaignTypeId)} {...{ handleToggleStatus, handleDelete, toggleStatusMutation, deleteCampaignMutation, setViewedCampaign }} />
                                    </Grid>
                                ));
                            })()}
                        </Grid>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    );


    if (isEmbedded) {
        return currentCampaigns
    }

    return wrapper((
        <SoftBox borderRadius="20px" color={"white"} width="100%" height={deviceLayout == 'mobile' ? 'calc(100svh - 4.25rem - 8px)' : "calc(100svh - 5.5rem - 16px)"} minHeight={deviceLayout == 'mobile' && "calc(100svh - 4.25rem - 8px)"}
            sx={({ palette: { white }, functions: { rgba } }) => ({ backgroundColor: rgba(white.main, 0.6), overflowY: 'hidden' })}
        >
            <SoftBox m={1}>

            </SoftBox>
            <SoftBox display="flex" sx={{ height: deviceLayout == 'mobile' ? `calc(100% - 16px)` : `calc(100% - 16px)` }}>
                {currentCampaigns}
            </SoftBox>
        </SoftBox>
    ), deviceLayout);
}

AllCampaignsView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
    isEmbedded: PropTypes.bool
}

export default AllCampaignsView;